import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';
import { t } from 'ttag';
import vocab from '../../../config/channels';
import { TChannel } from '../../../contexts/operator/operator-context';
import useOperatorContext from '../../../contexts/operator/useOperatorContext';
import useSiteContext from '../../../contexts/site/useSiteContext';
import useDeliveryVenueContext from '../../../contexts/delivery-venue/useDeliveryVenueContext';
import PageSpinnerThree from '../../general/PageSpinnerThree';
import AppLayout from '../../layouts/app/AppLayout';
import MenuPage from '../Menu/MenuPage';
import SiteSelectModal from './modal/SiteSelectModal';
import { useSiteSelectView } from '../../../services/siteSelectTracking';
import { singleSiteRedirect } from './singleSiteRedirect';
import useChannelSelectSetup from './useChannelSelectSetup';

type ChannelSelectModalPageProps = {
    channel?: TChannel | null;
};

// The landing page, as a modal over the menu of the first site: choosing a site to collect from changes
// the menu behind it.
const ChannelSelectModalPage = ({ channel }: ChannelSelectModalPageProps) => {
    const operatorContext = useOperatorContext();
    const siteContext = useSiteContext();
    const deliveryVenueContext = useDeliveryVenueContext();

    const [chosenHandle, setChosenHandle] = useState<string | null>(null);

    useChannelSelectSetup();

    const enabledChannels: TChannel[] = channel === 'dine_in'
        ? [vocab.dineIn.name]
        : [vocab.collection.name, vocab.delivery.name, vocab.catering.name, vocab.venues.name]
            .filter((name) => operatorContext.operator?.channels[name]);

    const redirect = siteContext?.sites ? singleSiteRedirect(siteContext.sites) : null;

    useSiteSelectView('modal', !!siteContext?.sites && !!deliveryVenueContext?.deliveryVenues && !redirect, {
        sites_count: siteContext?.sites?.length,
        channel_tabs: enabledChannels.length,
        start_tab: channel && enabledChannels.includes(channel) ? channel : enabledChannels[0],
    });

    if (!siteContext || !siteContext.sites || !deliveryVenueContext || !deliveryVenueContext.deliveryVenues) {
        return <PageSpinnerThree show={true} />;
    }

    const sites = siteContext.sites;

    if (redirect) {
        return <Navigate to={redirect} replace />;
    }

    // A menu needs a site to show before it can be ordered from, which delivery and dine in only get
    // from a postcode or a table, so it's the collection menu that goes behind the modal
    const previewHandle = channel === 'dine_in'
        ? undefined
        : chosenHandle ?? sites.find((site) => site.channels.collection)?.handle;

    return (
        <>
            <Helmet>
                <title>
                    {operatorContext.operator?.name ?? ''} {t`Online Ordering - Powered by Orderswift`}
                </title>
                <link rel="icon" href={operatorContext.operator?.theme?.v4.favicon ?? ''} />
            </Helmet>

            { previewHandle ? (
                <MenuPage channel={vocab.collection.name} restaurantId={previewHandle} preview />
            ) : (
                <AppLayout page="channels">
                    <div />
                </AppLayout>
            )}

            <SiteSelectModal
                channel={channel}
                enabledChannels={enabledChannels}
                sites={sites}
                deliveryVenues={deliveryVenueContext.deliveryVenues}
                onSelectSite={(site) => setChosenHandle(site.handle)}
            />
        </>
    );
};

export default ChannelSelectModalPage;
